import React, { useCallback, useEffect, useState } from 'react';
import { adminService, type ConnectTrace } from '../../services/adminService';
import LoadingSpinner from '../UI/LoadingSpinner';

/**
 * Where the time went between dialling a merchant and an agent's voice
 * reaching them — and, when nobody was connected, which agents were considered
 * and why each was passed over.
 *
 * Admin-only. The candidate list is a snapshot of every agent's availability,
 * lock and WebRTC state at that instant, which is not an agent's business to
 * read.
 */

interface Props {
  callAttemptId: string;
}

/**
 * Keys narrowed to the timestamp columns only. Typed as `keyof ConnectTrace`
 * this list would happily accept `retry_count`, and the read below would cast a
 * number to a date string without a word from the compiler.
 */
type StampKey = {
  [K in keyof ConnectTrace]: K extends `${string}_at` ? K : never;
}[keyof ConnectTrace];

/**
 * Ordered so each row's gap is measured from the row above it.
 *
 * `bridge_completed_at` is the agent picking up, and this table used to label
 * it "Two-way audio". It is not: Telnyx still has to join the two legs after
 * the agent answers, and that gap — `audio_ready_at` minus
 * `bridge_completed_at` — is silence the merchant hears and this panel was
 * hiding. The two are separate rows now, and the headline below measures to
 * the later one.
 */
const STEPS: Array<{ key: StampKey; label: string }> = [
  { key: 'dial_started_at', label: 'Dial started' },
  { key: 'merchant_answered_at', label: 'Merchant answered' },
  { key: 'agent_lookup_at', label: 'Agent lookup' },
  { key: 'agent_reserved_at', label: 'Agent reserved' },
  { key: 'bridge_requested_at', label: 'Bridge requested' },
  { key: 'bridge_completed_at', label: 'Agent picked up' },
  { key: 'audio_ready_at', label: 'Two-way audio' },
];

function ms(from: string | null, to: string | null): number | null {
  if (!from || !to) return null;
  const delta = new Date(to).getTime() - new Date(from).getTime();
  return Number.isFinite(delta) ? delta : null;
}

/** Sub-second in ms, above that in seconds — the eye reads 1.4s faster than 1400ms. */
function formatGap(value: number | null): string {
  if (value === null) return '—';
  if (value < 0) return '—';
  return value < 1000 ? `${value}ms` : `${(value / 1000).toFixed(1)}s`;
}

function formatClock(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleTimeString(undefined, { hour12: false }) +
        '.' +
        String(d.getMilliseconds()).padStart(3, '0');
}

/**
 * The headline number: merchant answer to two-way audio. This is the gap the
 * merchant experiences as silence, so it is shown on its own rather than left
 * to be added up from the rows.
 *
 * Measured to `audio_ready_at`, falling back to the agent's pickup only when
 * the audio stamp is absent — an instance whose migration has not run, or a
 * call traced before the column existed. The fallback is labelled, because
 * reporting the shorter number under the same name is how this panel came to
 * understate the silence in the first place.
 */
function AnswerToAudio({ trace }: { trace: ConnectTrace }) {
  const toAudio = ms(trace.merchant_answered_at, trace.audio_ready_at);
  const toPickup = ms(trace.merchant_answered_at, trace.bridge_completed_at);
  const value = toAudio ?? toPickup;
  const measuredToPickupOnly = toAudio === null && toPickup !== null;
  if (value === null) return null;
  // 1s is the target the client set; 3s was the measured median before this work.
  const tone =
    value <= 1000
      ? 'text-green-700 dark:text-green-400'
      : value <= 3000
        ? 'text-orange-700 dark:text-orange-400'
        : 'text-red-700 dark:text-red-400';
  return (
    <div className="mb-3">
      <div className="text-xs text-muted-foreground">
        {measuredToPickupOnly ? 'Answer to agent pickup' : 'Answer to two-way audio'}
      </div>
      <div className={`text-lg font-semibold ${tone}`}>{formatGap(value)}</div>
      {measuredToPickupOnly && (
        <div className="text-[11px] text-muted-foreground">
          No audio stamp on this call — the real silence is slightly longer.
        </div>
      )}
    </div>
  );
}

const CallConnectTiming: React.FC<Props> = ({ callAttemptId }) => {
  const [trace, setTrace] = useState<ConnectTrace | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setTrace(await adminService.getConnectTrace(callAttemptId));
    } catch {
      // Distinct from "no trace": one is a call we never traced, the other is
      // a request that failed. Saying "no data" for a failure would be a lie.
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [callAttemptId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (failed) {
    return (
      <div className="py-3 text-xs text-muted-foreground">
        Could not load connect timing.{' '}
        <button type="button" className="underline" onClick={() => void load()}>
          Retry
        </button>
      </div>
    );
  }

  if (!trace) {
    return (
      <div className="py-3 text-xs text-muted-foreground">
        No connect trace for this call. Only human auto-dial calls are traced.
      </div>
    );
  }

  const rejected = Object.entries(trace.reject_reasons ?? {});
  // Agents the dialer counted as available whose browser could not have taken
  // the call. If this list is non-empty on a call that found "no agent", the
  // pool was not short — we were routing to someone unreachable.
  const unreachable = (trace.candidates ?? []).filter(
    (c) => c.state === 'AVAILABLE' && (c.rtcReadiness === 'not_ready' || c.rtcReadiness === 'stale')
  );

  return (
    <div className="rounded-md border border-border bg-muted/30 p-3">
      <AnswerToAudio trace={trace} />

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="pb-1 pr-3 font-medium">Step</th>
              <th className="pb-1 pr-3 font-medium">Time</th>
              <th className="pb-1 font-medium">+</th>
            </tr>
          </thead>
          <tbody>
            {STEPS.map((step, i) => {
              const at = trace[step.key];
              const prev = i === 0 ? null : trace[STEPS[i - 1]!.key];
              return (
                <tr key={String(step.key)} className="border-t border-border/50">
                  <td className="py-1 pr-3">{step.label}</td>
                  <td className="py-1 pr-3 tabular-nums">{formatClock(at)}</td>
                  <td className="py-1 tabular-nums text-muted-foreground">
                    {i === 0 ? '' : formatGap(ms(prev, at))}
                  </td>
                </tr>
              );
            })}
            {trace.bridge_failed_at && (
              <tr className="border-t border-border/50">
                <td className="py-1 pr-3 text-red-700 dark:text-red-400">Failed</td>
                <td className="py-1 pr-3 tabular-nums">{formatClock(trace.bridge_failed_at)}</td>
                <td className="py-1" />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <dt className="text-muted-foreground">Outcome</dt>
        <dd>{trace.final_reason ?? 'In flight'}</dd>
        {trace.agent_disposition && (
          <>
            <dt className="text-muted-foreground">Agent tagged</dt>
            <dd>{trace.agent_disposition}</dd>
          </>
        )}
        {trace.retry_count !== null && (
          <>
            <dt className="text-muted-foreground">Retries</dt>
            <dd>{trace.retry_count}</dd>
          </>
        )}
      </dl>

      {unreachable.length > 0 && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 p-2 text-xs dark:border-red-800 dark:bg-red-900/30">
          <div className="font-medium text-red-800 dark:text-red-300">
            {unreachable.length} agent{unreachable.length > 1 ? 's' : ''} counted as available with
            no live WebRTC client
          </div>
          <div className="mt-0.5 text-red-700 dark:text-red-400">
            A call routed to {unreachable.length > 1 ? 'these agents' : 'this agent'} could not have
            been answered.
          </div>
        </div>
      )}

      {rejected.length > 0 && (
        <div className="mt-3">
          <div className="mb-1 text-xs font-medium text-muted-foreground">
            Agents passed over ({rejected.length})
          </div>
          <ul className="space-y-0.5 text-xs">
            {rejected.map(([agentId, reason]) => (
              <li key={agentId} className="flex justify-between gap-3">
                <span className="truncate font-mono text-[11px] text-muted-foreground">
                  {agentId.slice(0, 8)}
                </span>
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CallConnectTiming;
